import { CursorDefinition, CursorEnum } from './type';
import cursorIcon from '@/assets/icons/cursor';
import { globalStores } from '@/editor/stores';
import { isSafari } from '@/utils/browser/platform';

const cursorDefinitions: Record<CursorEnum, CursorDefinition> = {
  [CursorEnum.DEFAULT]: {
    path: cursorIcon.default,
    path2x: cursorIcon.default2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 6, y: 3 }
  },
  [CursorEnum.POINTER]: {
    path: cursorIcon.pointer,
    path2x: cursorIcon.pointer2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 9, y: 3 }
  },
  [CursorEnum.CROSSHAIR]: {
    path: cursorIcon.crosshair,
    path2x: cursorIcon.crosshair2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 12, y: 12 }
  },
  [CursorEnum.MOVE]: {
    path: cursorIcon.move,
    path2x: cursorIcon.move2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 12, y: 12 }
  },
  [CursorEnum.ALL_SCROLL]: {
    path: cursorIcon.allScroll,
    path2x: cursorIcon.allScroll2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 12, y: 12 }
  },
  [CursorEnum.GRABBING]: {
    path: cursorIcon.grabbing,
    path2x: cursorIcon.grabbing2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 12, y: 12 }
  },
  [CursorEnum.RESIZE_HORIZONTAL]: {
    path: cursorIcon.resizeEWRaw,
    path2x: cursorIcon.resizeEWRaw2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 12, y: 12 }
  },
  [CursorEnum.NOT_ALLOWED]: {
    path: cursorIcon.notAllowed,
    path2x: cursorIcon.notAllowed2x,
    size: { width: 28, height: 40 },
    hotSpot: { x: 4, y: 2.5 }
  },
  [CursorEnum.WALL_SPLIT]: {
    path: cursorIcon.wallSplit,
    path2x: cursorIcon.wallSplit2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 4, y: 18 }
  },
  [CursorEnum.WALL_JOIN]: {
    path: cursorIcon.wallJoin,
    path2x: cursorIcon.wallJoin2x,
    size: { width: 26, height: 24 },
    hotSpot: { x: 4, y: 4 }
  },
  [CursorEnum.MULTI_SELECT]: {
    path: cursorIcon.multiSelect,
    path2x: cursorIcon.multiSelect2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 4, y: 3 }
  },
  [CursorEnum.ARRAY_MATRIX]: {
    path: cursorIcon.arrayMatrix,
    path2x: cursorIcon.arrayMatrix2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 4, y: 3 }
  },
  [CursorEnum.ARRAY_LINE]: {
    path: cursorIcon.arrayLine,
    path2x: cursorIcon.arrayLine2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 4, y: 3 }
  },
  [CursorEnum.MEASURE]: {
    path: cursorIcon.measure,
    path2x: cursorIcon.measure2x,
    size: { width: 24, height: 25 },
    hotSpot: { x: 4, y: 30 }
  },
  [CursorEnum.DRAW_AREA_DIMENSION]: {
    path: cursorIcon.areaDimension,
    path2x: cursorIcon.areaDimension2x,
    size: { width: 24, height: 24 },
    hotSpot: { x: 12, y: 12 }
  },
  [CursorEnum.DRAW_LINEAR_DIMENSION]: {
    path: cursorIcon.linearDimension,
    path2x: cursorIcon.linearDimension2x,
    size: { width: 28, height: 28 },
    hotSpot: { x: 11, y: 13 }
  }
};

class CursorManager {
  private cursors: Map<CursorEnum, CursorDefinition> = new Map();
  private currentCursor: CursorEnum | null;
  private rotation: number | undefined;
  private _isSafari: boolean = isSafari();

  constructor() {
    this.currentCursor = null;
    this.registerCursors(cursorDefinitions);
  }

  /**
   * 批量注册光标
   * @param cursorDefinitions 光标定义对象
   */
  private registerCursors(cursorDefinitions: Record<CursorEnum, CursorDefinition>) {
    (Object.entries(cursorDefinitions) as TObjectEntries<Record<CursorEnum, CursorDefinition>>).forEach(
      ([style, definition]) => {
        this.registerCursor(style, definition);
      }
    );
  }

  /**
   * 从不同目录注册光标
   * @param style 光标样式名称
   * @param path 路径
   * @param options 可选配置
   */
  private registerCursor(style: CursorEnum, options: CursorDefinition) {
    this.cursors.set(style, { ...options });
  }

  /**
   * 设置当前光标
   * @param style 已注册的光标样式名称
   * @param rotation 旋转弧度
   */
  public setCursor(style: CursorEnum, rotation?: number) {
    if (this.currentCursor === style && this.rotation === rotation) return;
    this.currentCursor = style;
    this.rotation = rotation;

    const domElement = globalStores.editorStateStore.sceneInfo.canvasElement;

    const cursorDef = this.cursors.get(style);
    if (!cursorDef) {
      console.warn(`Cursor "${style}" not registered`);
      return;
    }

    // 设置自定义光标
    if (style === CursorEnum.RESIZE_HORIZONTAL) {
      domElement.style.cursor = this._getRotatedCursorStyle(cursorDef, rotation || 0);
    } else {
      domElement.style.cursor = this._getCursorStyle(cursorDef);
    }
  }

  private _getCursorStyle(cursorDef: CursorDefinition) {
    const cursorPath = `${cursorDef.path}`;
    const cursorPath2x = `${cursorDef.path2x}`;
    // -webkit-image-set 兼容性：chrome21+ safari6+ firefox88+
    // 使用 -webkit-image-set 来支持不同分辨率的光标图片，确保在高分辨率屏幕上 Safari 显示清晰
    return this._isSafari
      ? `-webkit-image-set(url(${cursorPath}) 1x, url(${cursorPath2x}) 2x) ${cursorDef.hotSpot?.x} ${cursorDef.hotSpot?.y}, auto`
      : `url(${cursorPath}) ${cursorDef.hotSpot?.x} ${cursorDef.hotSpot?.y}, auto`;
  }

  private _getRotatedCursorStyle(cursorDef: CursorDefinition, rotation: number) {
    const svgText = cursorDef.path.replace('$$transform$$', `rotate(${(rotation * 180) / Math.PI} 12 12)`);
    const svgText2x = cursorDef.path2x.replace('$$transform$$', `rotate(${(rotation * 180) / Math.PI} 12 12)`);
    const cursorUrl = `url(data:image/svg+xml;base64,${btoa(svgText)})`;
    const cursorUrl2x = `url(data:image/svg+xml;base64,${btoa(svgText2x)})`;
    return this._isSafari
      ? `-webkit-image-set(${cursorUrl} 1x, ${cursorUrl2x} 2x) ${cursorDef.hotSpot?.x} ${cursorDef.hotSpot?.y}, auto`
      : `${cursorUrl} ${cursorDef.hotSpot?.x} ${cursorDef.hotSpot?.y}, auto`;
  }
}

export const cursorManager = new CursorManager();
